import React from "react";
import classNames from "classnames";
import { clone } from "lodash";
import isEqual from "lodash/isEqual";
import { SortOrder } from "primereact/api";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { Column } from "primereact/column";
import { ContextMenu } from "primereact/contextmenu";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { Skeleton as RPSkeleton } from "primereact/skeleton";
import { ToastMessage } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  Navigate,
  NavigateOptions,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useLogUIEventOnMount } from "../../../amplitude";
import { LOB } from "../../../api/routes";
import { ClientId, featureEnabled } from "../../../client-features";
import { useClientId, useFeaturesForClientId } from "../../../ClientConfig";
import { PantheonAvatarWithoutMenu } from "../../../components/avatar/Avatar";
import { CommentContainer } from "../../../components/comments/Comment";
import { CRMContact } from "../../../components/crm-contact/CRMContact";
import { mmddyyyyToDate } from "../../../date-utils";
import { EffectiveDateFilter } from "../../../components/filters/EffectiveDateFilter";
import {
  AssigneeFilterDropdown,
  FilterDropdown,
  StatusFilterDropdown,
  UNASSIGNED_USER,
} from "../../../components/filters/Filters";
import { StateCards } from "../../../components/states/StateCards";
import {
  fakeCompanyNameFromCompanyName,
  usePrivacyMode,
} from "../../../context-providers/Privacy";
import { useToast } from "../../../context-providers/Toast";
import { useNavigateWithReloadCheck } from "../../../context-providers/UpdateContext";
import { IfFeatureEnabled } from "../../../IfFeatureEnabled";
import { ROUTES } from "../../../routes";
import {
  displayNameForUser,
  ExpandUnion,
  isDefined,
  LeafPaths,
} from "../../../util";
import { AppetiteIcon } from "../appetite/AppetiteIcon";
import { SafeReportMetadataResponse } from "../Conversion";
import {
  useComments,
  useLocalUser,
  useReports,
  useSafeReports,
  useUpdatingReports,
  useUsers,
} from "../data";
import { MapModalForSearch } from "../map/MapModal";
import { useMapModal } from "../map/MapModalContext";
import { formatAbbreviatedMoney } from "../Money";
import { SpecialtyReport } from "../SpecialtyReport";
import { brokerSortValue } from "../utils";
import styles from "./Dashboard.module.css";
import {
  defaultFilterForLobsAndArchived,
  deserializeFilter,
  Filter,
  SORT_FIELD_NAME,
} from "./filter";
import { useFilters } from "./filter-hooks";
import { AddFilterModal, ManageFilterModal } from "./FilterModal";
import { lobName } from "./lob";
import { IntegrationJobsCell } from "./IntegrationJobsCell";
import { ReportActionMenu, useReportActionMenuItems } from "./ReportActionMenu";
import { SubmissionAnalytics } from "./analytics/SubmissionAnalytics";
import { PolicyFlyDashboard } from "./PolicyFlyDashboard";
import { SearchAddresses } from "./SearchAddresses";
import { SubmissionHistory } from "./submission-history/SubmissionHistory";
import { StatusIcon } from "./StatusIcon";
import {
  columnPtOptions,
  getAllStatuses,
  numericAppetiteScore,
  readableAppetiteFromScore,
  readableStatus,
  statusIsDeclined,
} from "./util";
import { GoogleGroupIcon } from "../../../components/google-group/GoogleGroupIcon";
import { BorBadge } from "../bor/BorBadge";
import { useBorModal } from "../bor/BorModalContext";
import { CreateBorModal, ReportOption } from "../bor/CreateBorModal";
import { isBorFullyComplete, useBorLetters } from "../bor/data";
import { BorLetterResponse } from "../../../client";

type RowData = SafeReportMetadataResponse | { __loading: true };
type GroupedRowData = RowData & { __group?: string };
const isLoadingState = (data: RowData): data is { __loading: true } =>
  "__loading" in data;

const Skeleton = () => {
  return (
    <div style={{ padding: "6px 0px" }}>
      <RPSkeleton height="33px" />
    </div>
  );
};

const reportIsNotInSearch = (
  report: SafeReportMetadataResponse,
  searchText: string
) => {
  const searchTextLower = searchText.toLowerCase();
  const companyLower = (
    report.report_metadata.company_info.company_name ?? ""
  ).toLowerCase();
  // Prefer report-level broker contact when searching with a company_info fallback
  const brokerLower = (
    report.broker_contact?.organization_name ||
    report.report_metadata.company_info.broker_name ||
    ""
  ).toLowerCase();

  const brokerAgentLower = (
    report.broker_contact?.full_name || ""
  ).toLowerCase();
  const brokerAgentEmailLower = (
    report.broker_contact?.email || ""
  ).toLowerCase();

  // TODO: what else should we search on?
  return !(
    companyLower.includes(searchTextLower) ||
    brokerLower.includes(searchTextLower) ||
    brokerAgentLower.includes(searchTextLower) ||
    brokerAgentEmailLower.includes(searchTextLower)
  );
};

const TOAST_INTEGRATION_TYPES = ["RERUN_REPORT", "POLICY_FLY"] as const;
type ToastIntegrationType = (typeof TOAST_INTEGRATION_TYPES)[number];

const isToastIntegrationType = (type: string): type is ToastIntegrationType =>
  TOAST_INTEGRATION_TYPES.includes(type as ToastIntegrationType);

export const Dashboard = ({ clientId }: { clientId: ClientId }) => {
  const user = useLocalUser();
  const features = useFeaturesForClientId(useClientId(), useNavigate());

  const toast = useToast();
  const updates = useUpdatingReports().data?.updates ?? [];
  const lastInProgress = useRef<Set<string>>(new Set());

  const lobsForUser = (user?.lobs ?? []) as LOB[];
  const reports = useSafeReports(lobsForUser)?.data;
  const reportIds = useMemo(
    () => (reports ?? []).map((r) => r.report_id),
    [reports]
  );
  useComments(reportIds);
  const reportsById: {
    [report_id: string]: SafeReportMetadataResponse | undefined;
  } = useMemo(() => {
    return Object.fromEntries((reports ?? []).map((r) => [r.report_id, r]));
  }, [reports]);

  const successSummary = (
    integrationType: ToastIntegrationType,
    name: string
  ) => {
    switch (integrationType) {
      case "POLICY_FLY":
        return `${name}: Policy Fly application generated!`;
      case "RERUN_REPORT":
        return `${name} finished!`;
    }
  };

  const successDetail = (
    integrationType: ToastIntegrationType,
    reportId: string,
    policyFlyUrl: string | null | undefined,
    removeToast: () => void
  ) => {
    switch (integrationType) {
      case "POLICY_FLY":
        return (
          <>
            The Policy Fly application has been created.{" "}
            {policyFlyUrl ? (
              <a href={policyFlyUrl} target="_blank" rel="noopener noreferrer">
                View Application
              </a>
            ) : (
              <Link
                to={ROUTES.DASHBOARD_REPORT(reportId)}
                onClick={removeToast}
              >
                View report.
              </Link>
            )}
          </>
        );
      case "RERUN_REPORT":
        return (
          <>
            Re-processing is done.{" "}
            <Link to={ROUTES.DASHBOARD_REPORT(reportId)} onClick={removeToast}>
              View it here.
            </Link>
          </>
        );
    }
  };

  const failureSummary = (
    integrationType: ToastIntegrationType,
    name: string
  ) => {
    switch (integrationType) {
      case "POLICY_FLY":
        return `${name}: Failed to generate Policy Fly application`;
      case "RERUN_REPORT":
        return `${name} failed to update`;
    }
  };

  const failureDetail = (
    integrationType: ToastIntegrationType,
    errorDescription: string | null | undefined
  ) => {
    if (errorDescription) {
      return <>{errorDescription}</>;
    }
    switch (integrationType) {
      case "POLICY_FLY":
        return (
          <>Something went wrong when generating the Policy Fly application.</>
        );
      case "RERUN_REPORT":
        return (
          <>Sorry, something went wrong while re-processing this submission.</>
        );
    }
  };

  useEffect(() => {
    const currentUpdates = new Map(updates.map((u) => [u.update_id, u]));
    // One Create click can enqueue multiple tasks (e.g. one per layer for
    // PolicyFly). When several finish on the same poll with the same
    // outcome+message, collapse them so the user sees a single toast
    // per (report, integration_type, outcome, message) rather than N.
    const shownFailureKeys = new Set<string>();
    const shownSuccessKeys = new Set<string>();
    for (const lastUpdate of lastInProgress.current) {
      const currentUpdate = currentUpdates.get(lastUpdate);
      if (currentUpdate?.outcome) {
        const outcome = currentUpdate.outcome;
        const name =
          reportsById[currentUpdate.report_id]?.report_metadata.company_info
            .company_name ?? "<Unknown company>";
        const id = currentUpdate.report_id;
        const type = currentUpdate.integration_type;
        if (!isToastIntegrationType(type)) continue;

        if (outcome === "success") {
          const successKey = `${id}|${type}`;
          if (shownSuccessKeys.has(successKey)) continue;
          shownSuccessKeys.add(successKey);
          const m: ToastMessage = {
            severity: "success",
            summary: successSummary(type, name),
            detail: successDetail(type, id, currentUpdate.policy_fly_url, () =>
              toast.remove(m)
            ),
            icon: "pi pi-verified",
            sticky: true,
          };
          toast.show(m);
        } else {
          const failureKey = `${id}|${type}|${currentUpdate.error_description ?? ""}`;
          if (shownFailureKeys.has(failureKey)) continue;
          shownFailureKeys.add(failureKey);
          toast.show({
            severity: "error",
            summary: failureSummary(type, name),
            detail: failureDetail(type, currentUpdate.error_description),
            icon: "pi pi-cross",
            sticky: true,
          });
        }
      }
    }

    lastInProgress.current = new Set(
      updates
        .filter(
          (u) =>
            !u.outcome &&
            u.running_user_id === user?.id &&
            isToastIntegrationType(u.integration_type)
        )
        .map((u) => u.update_id)
    );
  }, [updates]);

  return (
    <Routes>
      <Route
        path="report/:reportId/:tabPath?/:tabInfo?"
        element={<SpecialtyReport clientId={clientId} />}
      />
      <Route path="analytics" element={<SubmissionAnalytics />} />
      <Route path="policyfly" element={<PolicyFlyDashboard />} />
      <Route path="reports" element={<Reports />} />
      <Route path={`search-addresses`} element={<SearchAddresses />} />
      <Route path="submission-history/*" element={<SubmissionHistory />} />
      <Route path="*" element={<Navigate to={features.defaultRoute(user)} />} />
    </Routes>
  );
};

type ReportKeys = ExpandUnion<LeafPaths<SafeReportMetadataResponse>>;

const SORT_COLUMN_NAME_MAPPING: [SORT_FIELD_NAME, ReportKeys][] = [
  ["company_name", "report_metadata.company_info.company_name"],
  // Use a synthetic per-row key for broker sorting, which can go away
  // once we support `sortFunction` again:
  ["broker_name", "__broker_sort_key" as ReportKeys],
  ["eff_date", "report_metadata.company_info.effective_date"],
  ["status", "status"],
  ["tiv", "report_metadata.precomputed_metadata.total_tiv"],
  ["creation_date", "created_at_ts"],
  ["target_premium", "report_metadata.company_info.target_premium"],
];

const updatedFilter = <K extends keyof Filter>(
  field: K,
  value: Filter[K],
  filter: Filter
): Filter => {
  const isEmpty = value === "" || (Array.isArray(value) && value.length === 0);
  return {
    ...filter,
    [field]: value,
    setFields: isEmpty
      ? filter.setFields.filter((f) => f !== field)
      : Array.from(new Set(filter.setFields.concat(field))),
  };
};

const Reports = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigateAndMaybeReload = useNavigateWithReloadCheck();
  const users = useUsers();
  const lobsForUser: LOB[] = (users.data?.current_user.lobs ?? []) as LOB[];
  const filter = useMemo(
    () => deserializeFilter(location.search, lobsForUser),
    [location.search]
  );

  // Use lobs from the filter. If none are included, just use all of the ones for the user
  const selectedLobs = lobsForUser.filter((lob) =>
    filter.selectedLobs.includes(lob)
  );

  const dataTableRef = useRef<DataTable<RowData[]>>(null);
  const contextMenuRef = useRef<ContextMenu>(null);
  const [contextMenuReport, setContextMenuReport] =
    useState<SafeReportMetadataResponse | null>(null);
  const [activeMenuReportId, setActiveMenuReportId] = useState<string | null>(
    null
  );
  const apiReports = useReports(selectedLobs);
  const allSpecialtyReports = useSafeReports(lobsForUser).data ?? [];
  const specialtyReports = useSafeReports(selectedLobs).data ?? [];
  const {
    visible: isMapModalVisible,
    showMapModal,
    hideMapModal,
  } = useMapModal();
  useLogUIEventOnMount("asgard_show_dashboard", { lobs: selectedLobs });

  // BOR state
  const { data: activeBorLetters } = useBorLetters(false);
  const borByReportId = useMemo(() => {
    const map: Record<string, BorLetterResponse> = {};
    for (const bor of activeBorLetters ?? []) {
      map[bor.cleared_report_id] = bor;
      map[bor.incoming_report_id] = bor;
    }
    return map;
  }, [activeBorLetters]);
  const [borModalReportId, setBorModalReportId] = useState<string | null>(null);
  const { showBorModal } = useBorModal();
  const borReportOptions: ReportOption[] = useMemo(
    () =>
      allSpecialtyReports.map((r) => ({
        reportId: r.report_id,
        label:
          r.report_metadata.company_info.named_insured ||
          r.report_metadata.company_info.company_name ||
          r.report_id,
      })),
    [allSpecialtyReports]
  );

  const navigateToFilter = (filter: Filter, options?: NavigateOptions) =>
    navigate(ROUTES.DASHBOARD_WITH_FILTER(filter), options);

  useEffect(() => {
    // Reset the data table after every change; this is the only way I've found
    // to make it pick up on sort changes.
    dataTableRef?.current?.reset();
  }, [filter]);

  const [searchText, setSearchText] = useState("");

  const clientId = useClientId();

  const selectedAssigneeUsers = filter.selectedAssigneeUserIds
    .map((id) => {
      if (id === "Unassigned") {
        return UNASSIGNED_USER;
      } else {
        const u = users?.data?.all_company_users.find((u) => u.id === id);
        return u ? { name: displayNameForUser(u), id: u.id } : undefined;
      }
    })
    .filter((u) => !!u);
  const selectedHandlerUsers = filter.selectedHandlerUserIds
    .map((id) => {
      if (id === "Unassigned") {
        return UNASSIGNED_USER;
      } else {
        const u = users?.data?.all_company_users.find((u) => u.id === id);
        return u ? { name: displayNameForUser(u), id: u.id } : undefined;
      }
    })
    .filter((u) => !!u);

  const statuses = getAllStatuses().map((status) => {
    return {
      name: readableStatus(status),
      code: status,
    };
  });
  const selectedStatuses = filter.selectedStatuses
    .map((status) => statuses.find((s) => s.code === status))
    .filter((s) => !!s);

  const appetites = [1, 2, 3, 5].map((appetite) => {
    return {
      name: readableAppetiteFromScore(appetite),
      code: appetite,
    };
  });
  const selectedAppetites = filter.selectedAppetites
    .map((appetite) => appetites.filter((a) => a.code.toString() === appetite))
    .filter((a) => !!a)
    .flatMap((a) => a);

  const brokerTeams = useMemo(() => {
    const teams = new Set<string>();
    for (const report of specialtyReports) {
      if (report.google_group_email) {
        teams.add(report.google_group_email);
      }
    }
    return Array.from(teams)
      .sort()
      .map((email) => ({
        name: email.split("@")[0] ?? "",
        code: email,
      }));
  }, [specialtyReports]);
  const selectedBrokerTeams = filter.selectedBrokerTeams
    .map((bt) => brokerTeams.find((t) => t.code === bt))
    .filter((t) => !!t);

  const initialSortField = filter.initialSortField;
  const initialSortOrder =
    filter.initialSortOrder === "asc" ? SortOrder.ASC : SortOrder.DESC;

  const [manageFilterModalOpen, setManageFilterModalOpen] = useState(false);
  const [addFilterModalState, setAddFilterModalState] = useState<Filter | null>(
    null
  );

  const filteredAndSortedReports: RowData[] = useMemo(() => {
    const filteredReports = specialtyReports.filter((report) => {
      if (selectedStatuses.length > 0) {
        if (!selectedStatuses.some((status) => status.code === report.status)) {
          return false;
        }
      }

      if (selectedAppetites.length > 0) {
        if (
          !selectedAppetites.some(
            (appetite) =>
              report.report_metadata.precomputed_metadata.appetite &&
              appetite.code ===
                numericAppetiteScore(
                  report.report_metadata.precomputed_metadata.appetite
                    .appetite_result
                )
          )
        ) {
          return false;
        }
      }

      if (selectedAssigneeUsers.length > 0) {
        if (
          !selectedAssigneeUsers.some(
            (user) =>
              (user.id === "Unassigned" && !report.assignee) ||
              user.id === report.assignee
          )
        ) {
          return false;
        }
      }

      if (selectedHandlerUsers.length > 0) {
        const handler = report.additional_assignees?.assignees["handler"];
        if (
          !selectedHandlerUsers.some(
            (user) =>
              (user.id === "Unassigned" && !handler) || user.id === handler
          )
        ) {
          return false;
        }
      }

      if (selectedBrokerTeams.length > 0) {
        if (
          !selectedBrokerTeams.some(
            (team) => team.code === report.google_group_email
          )
        ) {
          return false;
        }
      }

      if (filter.effectiveDateStart || filter.effectiveDateEnd) {
        const effDate = mmddyyyyToDate(
          report.report_metadata.company_info.effective_date
        );
        if (!effDate) {
          return false;
        }
        if (filter.effectiveDateStart) {
          const startDate = mmddyyyyToDate(filter.effectiveDateStart);
          if (startDate && effDate < startDate) {
            return false;
          }
        }
        if (filter.effectiveDateEnd) {
          const endDate = mmddyyyyToDate(filter.effectiveDateEnd);
          if (endDate && effDate > endDate) {
            return false;
          }
        }
      }

      if (searchText !== "") {
        if (reportIsNotInSearch(report, searchText)) {
          return false;
        }
      }

      return true;
    });

    return filteredReports;
  }, [
    selectedStatuses,
    specialtyReports,
    searchText,
    selectedAssigneeUsers,
    selectedHandlerUsers,
    selectedAppetites,
    selectedBrokerTeams,
    filter.effectiveDateStart,
    filter.effectiveDateEnd,
  ]);

  // If the user is searching and there are results outside of their currently
  // filtered bunch, we should include them here
  const allReportsFilteredBySearch: RowData[] = useMemo(() => {
    if (searchText === "") {
      return [];
    }

    const filteredAndSortedIds = new Set(
      filteredAndSortedReports.map((r) =>
        isLoadingState(r) ? "" : r.report_id
      )
    );

    return allSpecialtyReports.filter((report) => {
      if (filteredAndSortedIds.has(report.report_id)) {
        return false;
      }

      if (reportIsNotInSearch(report, searchText)) {
        return false;
      }

      return true;
    });
  }, [searchText, allSpecialtyReports, filteredAndSortedReports]);

  const groupedReportsForTable: GroupedRowData[] = useMemo(() => {
    if (apiReports.isPending) {
      return [
        { __loading: true },
        { __loading: true },
        { __loading: true },
        { __loading: true },
        { __loading: true },
        { __loading: true },
        { __loading: true },
        { __loading: true },
      ] as GroupedRowData[];
    }

    const grouped: GroupedRowData[] = [];

    // Add filtered reports with their group label
    if (filteredAndSortedReports.length > 0) {
      grouped.push(
        ...filteredAndSortedReports.map((report) => ({
          ...report,
          __group: "Currently filtered submissions",
        }))
      );
    }

    // Add search-only reports if they exist
    if (allReportsFilteredBySearch.length > 0) {
      grouped.push(
        ...allReportsFilteredBySearch.map((report) => ({
          ...report,
          __group: "Submissions outside of filter",
        }))
      );
    }

    return grouped;
  }, [
    apiReports.isPending,
    filteredAndSortedReports,
    allReportsFilteredBySearch,
  ]);

  // Augment rows with a synthetic primitive key for broker sorting. This
  // can likely go away once we support support sortFunction again in the DataTable.
  const augmentedReportsForTable: GroupedRowData[] = useMemo(() => {
    return groupedReportsForTable.map((r) => {
      if (isLoadingState(r)) return r;
      const report = r as SafeReportMetadataResponse & {
        __broker_sort_key?: string;
      };
      const key = brokerSortValue(
        report.broker_contact,
        report.report_metadata.company_info
      );
      return {
        ...report,
        __broker_sort_key: key,
        __group: r.__group,
      };
    });
  }, [groupedReportsForTable]);

  const menuRef = useRef<Menu>(null);

  const savedFilters = useFilters();
  const isUsingSavedQuery = savedFilters.find(({ filter: saved }) =>
    isEqual(filter, saved)
  );

  const model: MenuItem[] = (
    [
      {
        label: "Filters",
        items: [
          { label: "Manage", command: () => setManageFilterModalOpen(true) },
          ...(isUsingSavedQuery
            ? [
                {
                  label: "Clear current filter",
                  command: () =>
                    navigateToFilter(
                      defaultFilterForLobsAndArchived(selectedLobs)
                    ),
                },
              ]
            : [
                {
                  label: "Save current view as filter",
                  command: () => setAddFilterModalState(filter),
                },
              ]),
        ],
      },
      savedFilters.length > 0 ? { separator: true } : undefined,
    ] as MenuItem[]
  )
    .filter(isDefined)
    .concat(
      savedFilters.map(
        ({ name, filter, isDefault, id }): MenuItem => ({
          icon: isUsingSavedQuery?.id === id ? "pi pi-check" : undefined,
          label: name + (isDefault ? " (default)" : ""),
          command: () => navigateToFilter(filter),
        })
      )
    );

  const filterMenuRef = useRef<Menu | null>(null);
  const { privacyModeEnabled } = usePrivacyMode();

  const statusColumn = (
    <Column
      columnKey="status"
      key="status"
      style={{ width: "160px", padding: "16px 8px" }}
      field="status"
      sortable
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        const bor = borByReportId[report.report_id];
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <StatusIcon status={report.status} />
            {bor && !isBorFullyComplete(bor) && (
              <BorBadge
                bor={bor}
                reportId={report.report_id}
                onClick={() => showBorModal(report.report_id)}
              />
            )}
          </div>
        );
      }}
      pt={columnPtOptions}
      header={() => (
        <div className={styles.horizontalContainer}>
          <span>Status</span>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <StatusFilterDropdown
              statuses={statuses}
              selectedStatuses={selectedStatuses}
              setSelectedStatuses={(statuses) =>
                navigateToFilter(
                  updatedFilter(
                    "selectedStatuses",
                    statuses.map((s) => s.code),
                    filter
                  )
                )
              }
            />
          </div>
        </div>
      )}
    />
  );

  const columns = [
    <Column
      columnKey="spacer"
      key="spacer"
      style={{ width: "32px", padding: "16px 8px" }}
      header=""
      pt={columnPtOptions}
    />,
    <Column
      columnKey="comments"
      key="comments"
      style={{ width: "76px", padding: "16px 8px" }}
      hidden={!featureEnabled(clientId, "comments")}
      header=""
      pt={columnPtOptions}
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        return (
          <div style={{ marginRight: "8px" }}>
            <CommentContainer
              reportName={
                privacyModeEnabled
                  ? fakeCompanyNameFromCompanyName(
                      report.report_metadata.company_info.company_name
                    )
                  : report.report_metadata.company_info.company_name
              }
              reportId={report.report_id}
              fetchEnabled={false}
            />
          </div>
        );
      }}
    />,
    featureEnabled(clientId, "status-column-on-left") && statusColumn,
    <Column
      columnKey="assignee"
      key="assignee"
      style={{ width: "160px", padding: "16px 8px" }}
      header={() => (
        <div className={styles.horizontalContainer}>
          <AssigneeFilterDropdown
            icon={"pi pi-user-edit"}
            selectedUsers={selectedAssigneeUsers}
            setSelectedUsers={(users) =>
              navigateToFilter(
                updatedFilter(
                  "selectedAssigneeUserIds",
                  users.map((u) => u.id),
                  filter
                )
              )
            }
          />
          <span>
            {featureEnabled(clientId, "assignee-named-underwriter")
              ? "Underwriter"
              : "Assignee"}
          </span>
        </div>
      )}
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        const assignee = users.data?.all_company_users.find(
          (u) => u.id === report.assignee
        );
        return (
          <PantheonAvatarWithoutMenu
            label={displayNameForUser(assignee)}
            showName={true}
          />
        );
      }}
      pt={columnPtOptions}
    />,
    featureEnabled(clientId, "handler-column") && (
      <Column
        columnKey="handler"
        key="handler"
        style={{ width: "160px", padding: "16px 8px" }}
        header={() => (
          <div className={styles.horizontalContainer}>
            <AssigneeFilterDropdown
              icon={"pi pi-users"}
              selectedUsers={selectedHandlerUsers}
              setSelectedUsers={(users) =>
                navigateToFilter(
                  updatedFilter(
                    "selectedHandlerUserIds",
                    users.map((u) => u.id),
                    filter
                  )
                )
              }
            />
            <span>Specialist</span>
          </div>
        )}
        body={(report: RowData) => {
          if (isLoadingState(report)) return <Skeleton />;
          const handler = users.data?.all_company_users.find(
            (u) => u.id === report.additional_assignees?.assignees["handler"]
          );
          return (
            <PantheonAvatarWithoutMenu
              label={displayNameForUser(handler)}
              showName={true}
            />
          );
        }}
        pt={columnPtOptions}
      />
    ),
    <Column
      columnKey="company_name"
      key="company_name"
      header="Company Name"
      style={{ width: "250px", padding: "16px 8px" }}
      field="report_metadata.company_info.company_name"
      sortable
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        return (
          <span className={styles.twoLineText}>
            {privacyModeEnabled
              ? fakeCompanyNameFromCompanyName(
                  report.report_metadata.company_info.company_name
                )
              : report.report_metadata.company_info.company_name}
          </span>
        );
      }}
      pt={columnPtOptions}
    />,
    <Column
      columnKey="states"
      key="states"
      hidden={!featureEnabled(clientId, "show-states")}
      header="States"
      style={{ width: "112px", padding: "16px 8px" }}
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        return (
          <StateCards
            states={
              report.report_metadata.precomputed_metadata
                .unique_location_states ?? []
            }
            reportId={report.report_id}
          />
        );
      }}
      pt={columnPtOptions}
    />,
    <Column
      columnKey="appetite"
      key="appetite"
      header={() => (
        <div className={styles.horizontalContainer}>
          <span>Appetite</span>
          <IfFeatureEnabled feature="appetite-filtering">
            <div
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <FilterDropdown
                options={appetites}
                selectedOptions={selectedAppetites}
                setSelectedOptions={(appetites) =>
                  navigateToFilter(
                    updatedFilter(
                      "selectedAppetites",
                      appetites.map((a) => a.code.toString()),
                      filter
                    )
                  )
                }
              />
            </div>
          </IfFeatureEnabled>
        </div>
      )}
      style={{ width: "180px", padding: "16px 8px" }}
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        return (
          <AppetiteIcon
            naicsInfo={report.report_metadata.naics_info}
            precomputedMetadata={report.report_metadata.precomputed_metadata}
            status={report.status}
            justifyContent="flex-start"
            fontSize="10px"
          />
        );
      }}
      pt={columnPtOptions}
    />,
    <Column
      columnKey="google_group_email"
      key="google_group_email"
      header={() => (
        <div className={styles.horizontalContainer}>
          <span>Broker Team</span>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <FilterDropdown
              options={brokerTeams}
              selectedOptions={selectedBrokerTeams}
              setSelectedOptions={(teams) =>
                navigateToFilter(
                  updatedFilter(
                    "selectedBrokerTeams",
                    teams.map((t) => t.code),
                    filter
                  )
                )
              }
              itemTemplate={(option) => (
                <GoogleGroupIcon groupName={option.code} />
              )}
            />
          </div>
        </div>
      )}
      style={{ width: "180px", padding: "16px 8px" }}
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        return <GoogleGroupIcon groupName={report.google_group_email} />;
      }}
      pt={columnPtOptions}
    />,
    <Column
      columnKey="brokerage"
      key="brokerage"
      hidden={!featureEnabled(clientId, "show-brokerage")}
      header="Broker"
      style={{ width: "186px", padding: "16px 8px" }}
      // Use the synthetic primitive key so DataTable can sort built-in
      // Once sortFunction is supported again, we can remove this hack.
      field="__broker_sort_key"
      sortField="__broker_sort_key"
      sortable
      // sortFunction could be useful here, but won't get called in our
      // current configuration

      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;

        const brokerageName =
          report.broker_contact?.organization_name ||
          report.report_metadata.company_info.broker_name;

        return (
          <>
            <CRMContact
              contact={report.broker_contact}
              brokerageName={brokerageName}
              reportId={report.report_id}
            />
          </>
        );
      }}
      pt={columnPtOptions}
    />,
    <Column
      columnKey="eff_date"
      key="eff_date"
      header={() => (
        <div className={styles.horizontalContainer}>
          <span>Effective Date</span>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <EffectiveDateFilter
              effectiveDateStart={filter.effectiveDateStart}
              effectiveDateEnd={filter.effectiveDateEnd}
              onChangeStart={(value) =>
                navigateToFilter(
                  updatedFilter("effectiveDateStart", value, filter)
                )
              }
              onChangeEnd={(value) =>
                navigateToFilter(
                  updatedFilter("effectiveDateEnd", value, filter)
                )
              }
              onClearBoth={() =>
                navigateToFilter(
                  updatedFilter(
                    "effectiveDateEnd",
                    "",
                    updatedFilter("effectiveDateStart", "", filter)
                  )
                )
              }
              onChangeBoth={(start, end) =>
                navigateToFilter(
                  updatedFilter(
                    "effectiveDateEnd",
                    end,
                    updatedFilter("effectiveDateStart", start, filter)
                  )
                )
              }
            />
          </div>
        </div>
      )}
      style={{ width: "160px", padding: "16px 8px" }}
      field="report_metadata.company_info.effective_date"
      sortable
      sortFunction={(event) => {
        const { data: rawData, order } = event;
        const dataOrLoading: RowData[] = rawData;

        if (dataOrLoading.some(isLoadingState)) {
          return dataOrLoading;
        }

        const data: SafeReportMetadataResponse[] =
          dataOrLoading as SafeReportMetadataResponse[];

        return [...data].sort((a, b) => {
          const effectiveDateA = a.report_metadata.company_info.effective_date;
          const effectiveDateB = b.report_metadata.company_info.effective_date;

          // Check if dates are valid
          const isValidA =
            effectiveDateA && !isNaN(new Date(effectiveDateA).getTime());
          const isValidB =
            effectiveDateB && !isNaN(new Date(effectiveDateB).getTime());

          // Handle cases where one or both dates are invalid
          if (!isValidA && !isValidB) return 0;
          if (!isValidA) return order === 1 ? -1 : 1;
          if (!isValidB) return order === 1 ? 1 : -1;

          // Both dates are valid, compare them normally
          const dateA = new Date(effectiveDateA);
          const dateB = new Date(effectiveDateB);
          const comparison = dateA.getTime() - dateB.getTime();

          return (order ?? 0) * comparison;
        });
      }}
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        // We could also consider doing this for dates that are a day or two away
        const isAsap =
          report.report_metadata.company_info.effective_date === "ASAP";

        return (
          <div className={styles.horizontalContainer}>
            <span>{report.report_metadata.company_info.effective_date}</span>
            {isAsap && (
              <i
                className="pi pi-clock"
                style={{ color: "var(--red-500)" }}
              ></i>
            )}
          </div>
        );
      }}
      pt={columnPtOptions}
    />,
    !featureEnabled(clientId, "status-column-on-left") && statusColumn,
    <Column
      columnKey="tiv"
      key="tiv"
      header="TIV"
      style={{ width: "112px", padding: "16px 8px" }}
      field="report_metadata.precomputed_metadata.total_tiv"
      sortable
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        const totalTiv = report.report_metadata.precomputed_metadata.total_tiv;
        return (
          <span className={styles.monoMoney}>
            {formatAbbreviatedMoney(totalTiv)}
          </span>
        );
      }}
      pt={columnPtOptions}
    />,
    featureEnabled(clientId, "target-premium-in-dashboard") && (
      <Column
        columnKey="target_prem"
        key="target_prem"
        header="Target Prem"
        style={{ width: "140px", padding: "16px 8px" }}
        field="report_metadata.company_info.target_premium"
        sortable
        body={(report: RowData) => {
          if (isLoadingState(report)) return <Skeleton />;
          const targetString =
            report.report_metadata.company_info.target_premium;
          const target = targetString && parseFloat(targetString);
          return (
            <div style={{ display: "flex" }}>
              <div className={styles.verticalContainer}>
                <span>
                  {target && !isNaN(target)
                    ? formatAbbreviatedMoney(target)
                    : null}
                </span>
              </div>
            </div>
          );
        }}
        pt={columnPtOptions}
        sortFunction={(event) => {
          const { data: rawData, order } = event;
          const dataOrLoading: RowData[] = rawData;

          if (dataOrLoading.some(isLoadingState)) {
            return dataOrLoading;
          }

          const data: SafeReportMetadataResponse[] =
            dataOrLoading as SafeReportMetadataResponse[];

          return [...data].sort((a, b) => {
            const premA = a.report_metadata.company_info.target_premium;
            const premB = b.report_metadata.company_info.target_premium;

            const isValidA = premA && !isNaN(parseFloat(premA));
            const isValidB = premB && !isNaN(parseFloat(premB));

            // Handle cases where one or both premiums are missing
            if (!isValidA && !isValidB) return 0;
            if (!isValidA) return order === 1 ? -1 : 1;
            if (!isValidB) return order === 1 ? 1 : -1;

            // Both premiums are valid, compare them normally
            const comparison = parseFloat(premA) - parseFloat(premB);
            return (order ?? 0) * comparison;
          });
        }}
      />
    ),
    <Column
      columnKey="integration_jobs"
      key="integration_jobs"
      header="Tasks"
      style={{ width: "100px", padding: "16px 8px" }}
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        return <IntegrationJobsCell reportId={report.report_id} />;
      }}
      pt={columnPtOptions}
    />,
    <Column
      columnKey="created_at"
      key="created_at"
      style={{ width: "80px", padding: "16px 8px" }}
      header={<i className="pi pi-calendar" />}
      field="created_at_ts"
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        const formatted = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
        }).format(new Date(report.created_at_ts + "Z"));
        return <span>{formatted}</span>;
      }}
      headerTooltip="Date uploaded to Polaris"
      headerTooltipOptions={{ showDelay: 500 }}
      pt={columnPtOptions}
      sortable
    />,
    <Column
      columnKey="menu"
      key="menu"
      header={
        <>
          <Menu
            model={[
              {
                label: "Search bound policies",
                icon: "pi pi-map",
                command: showMapModal,
              },
            ]}
            popup
            ref={menuRef}
            id="popup_menu"
            popupAlignment="left"
          />
          <Button
            icon="pi pi-ellipsis-v"
            onClick={(event) => menuRef.current?.toggle(event)}
            aria-controls="popup_menu"
            aria-haspopup
            text
            severity="secondary"
            style={{ color: "var(--text-color)" }}
          />
        </>
      }
      style={{ width: "60px", padding: "16px 8px" }}
      body={(report: RowData) => {
        if (isLoadingState(report)) return <Skeleton />;
        return (
          <ActionMenu
            report={report}
            setActiveMenuReportId={setActiveMenuReportId}
            onCreateBor={
              borByReportId[report.report_id]
                ? undefined
                : () => setBorModalReportId(report.report_id)
            }
            onManageBor={
              borByReportId[report.report_id]
                ? () => showBorModal(report.report_id)
                : undefined
            }
          />
        );
      }}
      pt={columnPtOptions}
    />,
    <Column
      columnKey="spacer2"
      key="spacer2"
      style={{ width: "32px", padding: "16px 8px" }}
      header=""
      pt={columnPtOptions}
    />,
  ].filter((c) => !!c);

  if (!users.data) {
    return null;
  }

  const shouldHideSearchGrouping =
    searchText === "" || allReportsFilteredBySearch.length === 0;
  return (
    <>
      <div className={styles.container}>
        <div className={styles.searchContainer}>
          <InputText
            className={styles.searchInput}
            placeholder={`Search...`}
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
          />
          <div className={styles.chipContainer}>
            {lobsForUser.length > 1 && (
              <>
                {lobsForUser.map((lob) => {
                  return (
                    <Chip
                      key={lob}
                      tabIndex={0}
                      role="button"
                      label={lobName[lob]}
                      onClick={() => {
                        let newLobs = clone(selectedLobs);
                        if (newLobs.includes(lob)) {
                          newLobs = newLobs.filter((l) => lob !== l);
                        } else {
                          newLobs.push(lob);
                        }
                        navigateToFilter(
                          updatedFilter("selectedLobs", newLobs, filter)
                        );
                      }}
                      className={
                        selectedLobs.includes(lob)
                          ? classNames(styles.chip, styles.chipSelected)
                          : styles.chip
                      }
                    />
                  );
                })}
              </>
            )}
            <Button
              style={{
                padding: "4px 12px",
                minWidth: "32px",
                height: "28px",
                borderRadius: "16px",
                marginLeft: "4px",
                fontSize: "14px",
              }}
              icon={isUsingSavedQuery ? "pi pi-filter-fill" : "pi pi-filter"}
              tooltip="Saved filters"
              tooltipOptions={{ showDelay: 500 }}
              severity="secondary"
              outlined
              onClick={(e) => filterMenuRef.current?.toggle(e)}
              iconPos="right"
              label={isUsingSavedQuery?.name}
            />
            <Menu
              model={model}
              ref={filterMenuRef}
              popup
              popupAlignment="left"
              style={{ minWidth: "300px" }}
            />
          </div>
        </div>
        <RowContextMenu
          ref={contextMenuRef}
          report={contextMenuReport}
          onHide={() => setActiveMenuReportId(null)}
          onCreateBor={
            contextMenuReport && !borByReportId[contextMenuReport.report_id]
              ? () => setBorModalReportId(contextMenuReport.report_id)
              : undefined
          }
          onManageBor={
            contextMenuReport && borByReportId[contextMenuReport.report_id]
              ? () => showBorModal(contextMenuReport.report_id)
              : undefined
          }
        />
        <div className={styles.rowsContainer}>
          <DataTable
            ref={dataTableRef}
            scrollable
            value={augmentedReportsForTable}
            scrollHeight="calc(100vh - 234px)"
            rowGroupMode={shouldHideSearchGrouping ? "" : "subheader"}
            groupRowsBy="__group"
            rowGroupHeaderTemplate={(data: GroupedRowData) => {
              if (isLoadingState(data) || shouldHideSearchGrouping) {
                return null;
              }

              return (
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "var(--text-color)",
                  }}
                >
                  {data.__group}
                </div>
              );
            }}
            rowClassName={(report: RowData) => {
              if (isLoadingState(report)) {
                return "";
              }
              const classes = [styles.row];
              if (
                statusIsDeclined(report.status) &&
                featureEnabled(clientId, "gray-out-declined-rows")
              ) {
                classes.push(styles.rowDeclined);
              }
              if (activeMenuReportId === report.report_id) {
                classes.push(styles.rowMenuActive);
              }

              return classNames(classes);
            }}
            paginator
            rows={30}
            sortMode="multiple"
            multiSortMeta={[
              { field: "__group", order: 1 },
              {
                field:
                  SORT_COLUMN_NAME_MAPPING.find(
                    ([field]) => field == initialSortField
                  )?.[1] || "creation_date",
                order: initialSortOrder,
              },
            ]}
            onSort={(properties) => {
              const multiSortMeta = properties["multiSortMeta"];
              if (multiSortMeta && Array.isArray(multiSortMeta)) {
                // Find the sort that's NOT the __group sort (that's our user's sort)
                const userSort = multiSortMeta.find(
                  (s) => s.field !== "__group"
                );

                if (userSort) {
                  let newFilter = filter;
                  if (userSort.order !== undefined) {
                    newFilter = updatedFilter(
                      "initialSortOrder",
                      userSort.order === 1 ? "asc" : "desc",
                      newFilter
                    );
                  }

                  if (userSort.field) {
                    const sort = SORT_COLUMN_NAME_MAPPING.find(
                      ([, path]) => path === userSort.field
                    )?.[0];
                    if (sort) {
                      newFilter = updatedFilter(
                        "initialSortField",
                        sort,
                        newFilter
                      );
                    } else {
                      throw new Error(
                        `Unrecognized sort field ${userSort.field}`
                      );
                    }
                  }

                  navigateToFilter(newFilter);
                }
              }
            }}
            pt={{
              headerRow: {
                style: { boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.15)" },
              },
            }}
            emptyMessage={"No results found"}
            onContextMenu={(event) => {
              const report = event.data as RowData;
              if (!isLoadingState(report)) {
                setContextMenuReport(report);
                setActiveMenuReportId(report.report_id);
                contextMenuRef.current?.show(event.originalEvent);
              }
            }}
            onRowClick={(event) => {
              const target = event.originalEvent.target as HTMLElement;
              if (target.closest("button")) return;
              const report = event.data as RowData;
              if (!isLoadingState(report)) {
                navigateAndMaybeReload(
                  ROUTES.DASHBOARD_REPORT(report.report_id, "general")
                );
              }
            }}
          >
            {columns}
          </DataTable>
        </div>
      </div>
      <ManageFilterModal
        visible={manageFilterModalOpen}
        onHide={() => setManageFilterModalOpen(false)}
      />
      <AddFilterModal
        filter={addFilterModalState}
        onHide={() => setAddFilterModalState(null)}
      />
      {isMapModalVisible && (
        <MapModalForSearch
          visible={isMapModalVisible}
          onHide={hideMapModal}
          lobs={selectedLobs}
        />
      )}
      <CreateBorModal
        visible={borModalReportId !== null}
        onHide={() => setBorModalReportId(null)}
        currentReportId={borModalReportId ?? ""}
        reportOptions={borReportOptions}
      />
    </>
  );
};

const ActionMenu = ({
  report,
  setActiveMenuReportId,
  onCreateBor,
  onManageBor,
}: {
  report: SafeReportMetadataResponse;
  setActiveMenuReportId: (id: string | null) => void;
  onCreateBor?: () => void;
  onManageBor?: () => void;
}) => {
  return (
    <ReportActionMenu
      report={report}
      isUpcomingRenewal={false}
      setActionMenuIsOpen={(isOpen) =>
        setActiveMenuReportId(isOpen ? report.report_id : null)
      }
      onCreateBor={onCreateBor}
      onManageBor={onManageBor}
    />
  );
};

const RowContextMenu = React.forwardRef<
  ContextMenu,
  {
    report: SafeReportMetadataResponse | null;
    onHide: () => void;
    onCreateBor?: () => void;
    onManageBor?: () => void;
  }
>(({ report, onHide, onCreateBor, onManageBor }, ref) => {
  const { items, dialogs } = useReportActionMenuItems(
    report,
    false,
    onCreateBor,
    onManageBor
  );
  return (
    <>
      <ContextMenu
        ref={ref}
        model={items}
        style={{ width: "232px" }}
        breakpoint="767px"
        onHide={onHide}
      />
      {dialogs}
    </>
  );
});
RowContextMenu.displayName = "RowContextMenu";
